<template lang="pug">
.overall-leaderboard.main-container-wide
  .leaderboard-select
    .tabs
      div(v-for="tab in tabs" :class="{ 'selected': tab === selectedTab }" @click="updateTab(tab)")
        span {{ __(tab, 'auto') }}
  Carousel.leaderboard-list(
    arrowOffset="0"
    controls="inside"
    theme="no-pad"
    ref="carousel"
  )
    OverallAthleteCard(:athletes="results")
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'
import Carousel from '../core/Carousel.vue'
import { useRogue } from '../../js/rogue'

const carouselRef = useTemplateRef<typeof Carousel>('carousel')
const rogue = useRogue()

type Division = {
  [key: string]: any,
  name: string,
  athletes: any[],
}

const props = withDefaults(defineProps<{
  arrowOffset?: string,
  compYear?: string,
  competitionID: string,
  divisions?: Division[],
}>(), {
  arrowOffset : '0px',
  compYear    : '2023',
  divisions   : () => [],
})

const results = ref<{[key: string]: any}[]>([{ }, { }, { }, { }, { }, { }])
const selectedTab = ref<string | undefined>((props.divisions.filter((division) => division.showOnOverallLeaderboard) || [{}])[0]?.name)

const tabs = computed(() => {
  return props.divisions.filter((division) => division.showOnOverallLeaderboard).map((division) => division.name)
})

async function loadOverallLeaderboard() {
  if (!props.compYear || !selectedTab.value) {
    return
  }
  const showSkeletons = setTimeout(() => {
    nextTick(() => results.value = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}])
  }, 50)
  const loadingTab = selectedTab.value
  try {
    if (!rogue) {
      return
    }
    
    const { data } = await rogue.fetch<{data: any}>(`/svc_event/leaderboard/${props.competitionID}/${props.compYear}/${selectedTab.value}/overall`)

    if(!data) {
      return
    }

    clearTimeout(showSkeletons)

    // If they changed tab before the data loaded, skip this set
    if (loadingTab !== selectedTab.value) {
      return
    }

    const selectedDivision = props.divisions.find((d) => d.name === selectedTab.value)
    results.value = data.overallStandings.placements.map((athleteInfo: any) => {
      const athlete = selectedDivision?.athletes.find((a) => a.name === athleteInfo.athlete.fullName)

      return ({
        name      : athleteInfo.athlete.fullName.split(' ').pop(),
        country   : athlete?.flag,
        placement : athleteInfo.placement,
        points    : athleteInfo.totalPoints,
        thumb     : athlete?.headshotImg.src,
      })
    })
  } catch (e) {
    // Error loading leaderboard
    console.error('Error loading leaderboard', e)
  }
}

async function updateTab(tab: string) {
  selectedTab.value = tab
  await loadOverallLeaderboard()
  carouselRef.value?.scrollTo(0, false)
}

onMounted(() => {
  loadOverallLeaderboard()
})
</script>

<style lang="scss" scoped>

$leaderboard-tab-width: 120px;
.overall-leaderboard {
  display: grid;
  height: 100px;

  grid-template-areas: 'select list';
  grid-template-columns: $leaderboard-tab-width minmax(0, 1fr);

  @include not-desktop(){
    &.main-container-wide{
      margin-left: 0;
      margin-right: 0;
    }
  }
}

.leaderboard-select {
  grid-area: select;
  width: $leaderboard-tab-width;
  overflow-y: auto;
}

.leaderboard-list {
  grid-area: list;
}

:deep(.leaderboard-list .controls .control-container) {
  left: 0;
  right: 0;
}

:deep(.leaderboard-list .controls .control-container .ctrl) {
  display: flex;
  align-items: center;
  background-color: $color-white;
  border-right: 1px solid $color-gray;
  border-left: 1px solid $color-gray;
  border-bottom: 1px solid $color-gray;
  height: 100%;
  opacity: 1;

  @include mobile() {
    display: none;
  }
}

:deep(.leaderboard-list .controls .control-container .ctrl:hover svg) {
  fill: #000000 !important;
}

.tabs {
  display: flex;
  flex-direction: column;
  height: 100px;
  width: $leaderboard-tab-width;
  border-left: 1px solid $color-gray;
  border-bottom: 1px solid $color-gray;

  @include mobile() {
    display: block;
    width: 100%;
  }

  div {
    font-size: $size-xxs;
    line-height: $size-s;
    color: $color-black;
    background: $color-white;
    padding-left: $spacing-xl;
    height: 50%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    cursor: pointer;

    @include mobile() {
      width: 100%;
      border-right: 0.5px solid $color-gray;
      border-top: 0.5px solid $color-gray;
    }

    &.selected {
      background: $color-black;
      color: $color-white;
      font-weight: $weight-bold;
      border: 1px solid $color-black;
    }
  }
}
</style>
